import React from 'react';
import { useSelector } from 'react-redux';
import {
	Utils as QbUtils,
	type _TreeI,
	type Fields,
	type ImmutableTree,
	type JsonLogicTree
} from '@react-awesome-query-builder/mui';

import { selectMainTheme } from 'app/store/fuse/settingsSlice';
import useSettings from './useSettings';

// eslint-disable-next-line import/no-cycle
import QueryBuilderConfig from '../filter/QueryBuilderConfig';

export interface QueryType {
	query: JsonLogicTree;
	currentQuery: JsonLogicTree;
	currentTree: ImmutableTree<_TreeI>;
	queryConfig: any;
	handleQueryChange: (newTree: any, conf: any) => void;
	handleQueryApply: VoidFunction;
	handleQueryClear: VoidFunction;
}

export interface QueryProps {
	module: string;
	defaultQuery?: JsonLogicTree;
}

export default function useQuery({ module, defaultQuery }: QueryProps) {
	const { settings, t } = useSettings(module);

	const mainTheme = useSelector(selectMainTheme);
	const config = QueryBuilderConfig({ t, mainTheme });

	config.fields = settings?.filter_types
		?.filter(filter => filter.type !== 'tags')
		.reduce((fields: Fields, filter: any) => {
			fields[filter.filt_field] = {
				label: t(filter.filt_label),
				valueSources: ['value'],
				type: filter.filt_type,
				tableName: filter.filt_table
			};
			return fields;
		}, {});

	const parseQuery: (newQuery?: JsonLogicTree) => JsonLogicTree | undefined = React.useCallback(
		newQuery =>
			defaultQuery
				? newQuery
					? { and: [{ locked: defaultQuery }, newQuery] }
					: { and: [{ locked: defaultQuery }] }
				: newQuery,
		[defaultQuery]
	);

	const defaultTree = defaultQuery ? QbUtils.loadFromJsonLogic(parseQuery(), config) : undefined;
	const initialTree = defaultTree ? defaultTree : QbUtils.loadTree({ id: QbUtils.uuid(), type: 'group' });

	const [query, setQuery] = React.useState<JsonLogicTree | undefined>(parseQuery());
	const [currentQuery, setCurrentQuery] = React.useState<JsonLogicTree | undefined>();
	const [currentTree, setCurrentTree] = React.useState(initialTree);

	const handleQueryChange = (newTree, conf) => {
		const formattedCurrentQuery = QbUtils.jsonLogicFormat(newTree, conf);
		setCurrentQuery(formattedCurrentQuery.logic);
		setCurrentTree(newTree);
	};

	function handleQueryApply() {
		setQuery(parseQuery(currentQuery));
	}

	function handleQueryClear() {
		setQuery(parseQuery());
		setCurrentQuery(undefined);
		setCurrentTree(initialTree);
	}

	return {
		query,
		currentQuery,
		currentTree,
		queryConfig: config,
		handleQueryChange,
		handleQueryApply,
		handleQueryClear
	} as QueryType;
}
